import { Box, Icon, Image, styled } from "@tm/components"
import { useUser } from "@tm/context-distribution"
import { AvailabilityStatus } from "@tm/models"
import { mapAvailabilityStatusToLevel } from "@tm/utils"

const mapFallbackImageToWmCatalog = (traderId?: number) => {
    switch (traderId) {
        case 226: // NL
        case 561: // NL Telesales
            return "/styles/wm/images/availState0_nl.png"
        case 1432: // DK
        case 1550: // DK - NDO
        case 1649: // DK Telesales
            return "/styles/wm/images/availState0_dk.png"
        case 1531: // IT
        case 1551: // IT Telesales
            return "/styles/wm/images/availState0_it.png"
        default:
            return "/styles/wm/images/availState_0.png"
    }
}

type AvailabilityProps = {
    status: AvailabilityStatus
    description?: string
    iconName?: string
    imageUrl?: string
    onClick?(): void
}

const Wrapper = styled(Box, { shouldForwardProp: (prop) => prop !== "hasImage" })(({ hasImage }: { hasImage: boolean }) => ({
    ...(hasImage && {
        width: "auto",
    }),
}))

const Container = styled(Box, { shouldForwardProp: (prop: string) => !["level", "hasImage"].includes(prop) })(
    ({ level, hasImage }: { level: number; hasImage: boolean }) => ({
        ...(level === 5 && {
            border: "1px solid #e2e2e2",
            borderTopLeftRadius: "3px",
            borderTopRightRadius: "3px",
            borderBottom: 0,
        }),
        ...(hasImage && {
            display: "none",
        }),
    })
)

const Description = styled(Box, { shouldForwardProp: (prop) => prop !== "hasImage" })(({ hasImage }: { hasImage: boolean }) => ({
    ...(hasImage && {
        border: 0,
        padding: 0,
    }),
}))

const StyledImage = styled(Image, { shouldForwardProp: (prop) => prop !== "isFallback" })(({ isFallback }: { isFallback?: boolean }) => ({
    ...(isFallback
        ? {
              width: "8rem",
              height: "2.75rem",
          }
        : {
              maxWidth: "none",
              maxHeight: "2.75em",
          }),
}))

const StyledIcon = styled(Icon)({
    size: "extrasmall",
    width: "2em",
    height: "2em",
})

export function AvailabilityComponent({ status, iconName, imageUrl, description, onClick }: AvailabilityProps) {
    const { userContext } = useUser() ?? {}

    const fallbackUrl = mapFallbackImageToWmCatalog(userContext.principal?.traderId)

    const level = mapAvailabilityStatusToLevel(status)

    let imageElement
    if (imageUrl) {
        imageElement = <StyledImage src={imageUrl} />
    } else if (iconName && level !== 5) {
        imageElement = <StyledIcon name={iconName} />
    }

    return (
        <Wrapper hasImage={!!imageUrl} onClick={onClick}>
            <Container level={level} hasImage={!!imageUrl} title={description} />
            <Description hasImage={!!imageUrl}>
                {(description || iconName || imageUrl) && (
                    <>
                        {imageElement ? <Box marginRight={0}>{imageElement}</Box> : <StyledImage src={fallbackUrl} isFallback />}
                        {description && <Box display={imageUrl || level === 5 ? "none" : undefined}>{description}</Box>}
                    </>
                )}
            </Description>
        </Wrapper>
    )
}
